/**
 * Staff Service
 *
 * Staff is a thin role record — personal data lives in Person.
 * See docs/api/BACKEND.md § Staff model for source of truth.
 */

import {
  Staff,
  StaffCreateFromPersonInput,
  StaffFormData,
  StaffHistoryEntry,
  StaffListFilters,
  StaffTerminationReport,
  StaffStatus,
  StaffPosition,
  StaffPositionFilters,
  StaffPositionUpsertInput,
  StaffScopeType,
  TokenSettlementMethod,
  PaginatedStaffResponse,
  StaffWithUser,
  StaffExpense,
  LocationStaffTokenConfig,
  LocationStaffTokenConfigInput,
  StaffTokenBalance,
  StaffTokenLedgerEntry,
  PendingTokenTransfer,
  TokenCompanySummary,
  ManualGrantInput,
  ManualAdjustmentInput,
  WeeklyGrantRunInput,
  StaffChargeSellableInput,
  StaffChargeSellableResult,
  CompanyStaffTokenConfig,
  CompanyStaffTokenConfigInput,
  StaffActivityRow,
  StaffActivityFilters,
  StaffActivityPage,
  ApproverCredential,
} from '../types';
import { mapBackendPerson, joinName } from '@/entities/person';
import { syncPersonPrimaryPhone } from '@/entities/person/services/personService';
import { apiClient } from '@/shared/services/api';
import { lookupWristband } from '@/shared/services/wristbandService';
import { getStaffTokenSummary } from './companyTokenService';
import { pickPrimaryTokenBalance } from '../utils/tokenBalanceSelection';
import type { ApiError } from '@/types/global';

/**
 * Snake-case the reinforced-authorization fields for the backend. Every
 * staff-wallet write sends exactly one factor + a reason; the server verifies
 * them against `location.authorize_staff_tokens`.
 */
function buildApproverPayload(input: ApproverCredential): Record<string, unknown> {
  return {
    reason: input.reason,
    ...(input.approverPassword ? { approver_password: input.approverPassword } : {}),
    ...(input.approverNfcUid ? { approver_nfc_uid: input.approverNfcUid } : {}),
  };
}

// ---------------------------------------------------------------------------
// Backend response shapes
// ---------------------------------------------------------------------------

/** Detail response: person is a nested object */
interface BackendStaffDetail {
  id: string;
  person: Record<string, unknown>;
  scope_type?: string;
  scopeType?: string;
  // Backend ``StaffSerializer`` ships the FK as ``location_id``
  // (camelCased to ``locationId`` by apiClient). ``location`` is
  // accepted as a legacy fallback because earlier code paths set it
  // and tests assert on it.
  locationId?: string | null;
  location?: string | null;
  staff_position?: Record<string, unknown> | null;
  staffPosition?: Record<string, unknown> | null;
  staff_position_id?: string | null;
  staffPositionId?: string | null;
  position: string;
  department: string;
  employmentType?: string;
  employment_type?: string;
  status: string;
  terminationDate?: string | null;
  termination_date?: string | null;
  terminationReason?: string;
  termination_reason?: string;
  hireDate?: string;
  hire_date?: string;
  hourlyRate: string | null;
  salary: string | null;
  isActive?: boolean;
  is_active?: boolean;
  tokenLocationBlacklist?: string[];
  token_location_blacklist?: string[];
  maxDebtOverride?: string | null;
  max_debt_override?: string | null;
  hideTokenBalanceInPos?: boolean | null;
  hide_token_balance_in_pos?: boolean | null;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
}

/** List response: name, email, avatarUrl are flattened */
interface BackendStaffList {
  id: string;
  personId?: string;
  person_id?: string;
  name?: string;
  email?: string;
  avatarUrl?: string | null;
  avatar_url?: string | null;
  scope_type?: string;
  scopeType?: string;
  staff_position?: Record<string, unknown> | null;
  staffPosition?: Record<string, unknown> | null;
  staff_position_id?: string | null;
  staffPositionId?: string | null;
  position: string;
  department: string;
  status: string;
  terminationDate?: string | null;
  employmentType?: string;
  employment_type?: string;
  // StaffListSerializer ships the FK as `location_id` → `locationId` via
  // apiClient. `location` is the legacy/object form; prefer locationId.
  location: string | null;
  locationId?: string | null;
  location_id?: string | null;
  is_active?: boolean;
  isActive?: boolean;
  // Plain column on the Staff row. Optional so a backend that predates it
  // degrades to "unknown" (blank cell) rather than a fabricated date.
  hireDate?: string | null;
  hire_date?: string | null;
  // Does this employee have a platform login? Boolean by design — see the
  // `get_has_user` docstring on shaka-api's StaffListSerializer. Optional so a
  // backend that predates the field degrades to "unknown" rather than "no".
  hasUser?: boolean;
  has_user?: boolean;
}

interface BackendStaffPosition {
  id: string;
  name: string;
  description?: string;
  scope_type?: string;
  scopeType?: string;
  location?: string | null;
  locationId?: string | null;
  suggests_platform_access?: boolean;
  suggestsPlatformAccess?: boolean;
  default_access_role_id?: string | null;
  defaultAccessRoleId?: string | null;
  default_access_role?: Record<string, unknown> | null;
  defaultAccessRole?: Record<string, unknown> | null;
  is_active?: boolean;
  isActive?: boolean;
  assigned_staff_count?: number;
  assignedStaffCount?: number;
}

interface BackendStaffExpense {
  id: string;
  staff: string;
  credential: string | null;
  sourceType?: string | null;
  source_type?: string | null;
  sourceObjectId?: string | null;
  source_object_id?: string | null;
  amount: string;
  description: string;
  status: 'open' | 'settled' | 'voided';
  settlementPayment?: string | null;
  settlement_payment?: string | null;
  pendingObligation?: string | null;
  pending_obligation?: string | null;
  settlementType?: string | null;
  settlement_type?: string | null;
  createdAt?: string;
  created_at?: string;
}

interface BackendLocationStaffTokenConfig {
  id: string;
  location: string;
  locationId?: string | null;
  location_id?: string | null;
  enabled: boolean;
  tokenValueAmount?: string;
  token_value_amount?: string;
  tokenValueCurrency?: string;
  token_value_currency?: string;
  weeklyGrantEnabled?: boolean;
  weekly_grant_enabled?: boolean;
  weeklyGrantAmount?: number;
  weekly_grant_amount?: number;
  weeklyGrantDayOfWeek?: number;
  weekly_grant_day_of_week?: number;
  weeklyGrantTime?: string | null;
  weekly_grant_time?: string | null;
  grantAfterDebtSettled?: boolean;
  grant_after_debt_settled?: boolean;
  carryOverEnabled?: boolean;
  carry_over_enabled?: boolean;
  carryOverMax?: number;
  carry_over_max?: number;
  debtEnabled?: boolean;
  debt_enabled?: boolean;
  debtLimit?: number;
  debt_limit?: number;
  debtOnGrantPolicy?: LocationStaffTokenConfig['debtOnGrantPolicy'];
  debt_on_grant_policy?: LocationStaffTokenConfig['debtOnGrantPolicy'];
  debtSettlementMethods?: LocationStaffTokenConfig['debtSettlementMethods'];
  debt_settlement_methods?: LocationStaffTokenConfig['debtSettlementMethods'];
  manualGrantsEnabled?: boolean;
  manual_grants_enabled?: boolean;
  transfersEnabled?: boolean;
  transfers_enabled?: boolean;
  transferRequiresApproval?: boolean;
  transfer_requires_approval?: boolean;
  newStaffGrantPolicy?: LocationStaffTokenConfig['newStaffGrantPolicy'];
  new_staff_grant_policy?: LocationStaffTokenConfig['newStaffGrantPolicy'];
  inactiveStaffPolicy?: LocationStaffTokenConfig['inactiveStaffPolicy'];
  inactive_staff_policy?: LocationStaffTokenConfig['inactiveStaffPolicy'];
  showTotalsInPos?: boolean;
  show_totals_in_pos?: boolean;
  debtAlertThreshold?: number;
  debt_alert_threshold?: number;
  createdAt: string;
  updatedAt: string;
}

interface BackendStaffTokenBalance {
  id: string;
  location?: string;
  locationId?: string | null;
  location_id?: string | null;
  staffId?: string;
  staff_id?: string;
  staff?: string;
  staffName?: string;
  staff_name?: string;
  availableTokens?: number;
  available_tokens?: number;
  debtTokens?: number;
  debt_tokens?: number;
  lastGrantAt?: string | null;
  last_grant_at?: string | null;
  updatedAt: string;
}

interface BackendStaffTokenLedgerEntry {
  id: string;
  location?: string;
  locationId?: string | null;
  location_id?: string | null;
  staffId?: string;
  staff_id?: string;
  staff?: string;
  staffName?: string;
  staff_name?: string;
  origin: StaffTokenLedgerEntry['origin'];
  status?: StaffTokenLedgerEntry['status'];
  tokenDelta?: number;
  token_delta?: number;
  availableAfter?: number;
  available_after?: number;
  debtAfter?: number;
  debt_after?: number;
  contentType?: number | null;
  content_type?: number | null;
  objectId?: string | null;
  object_id?: string | null;
  notes?: string;
  metadata?: Record<string, unknown>;
  createdById?: string | null;
  created_by_id?: string | null;
  expiresAt?: string | null;
  expires_at?: string | null;
  createdAt: string;
}

function readField<T>(record: Record<string, unknown>, keys: string[], fallback: T): T {
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null) return value as T;
  }
  return fallback;
}

function mapStaffPositionRaw(raw: Record<string, unknown> | null | undefined): StaffPosition | null {
  if (!raw) return null;
  return {
    id: readField<string>(raw, ['id'], ''),
    name: readField<string>(raw, ['name'], ''),
    description: readField<string>(raw, ['description'], ''),
    scopeType: readField<StaffPosition['scopeType']>(raw, ['scope_type', 'scopeType'], 'location'),
    locationId: readField<string | null>(raw, ['location', 'locationId'], null),
    isActive: readField<boolean>(raw, ['is_active', 'isActive'], true),
    suggestsPlatformAccess: readField<boolean>(raw, ['suggests_platform_access', 'suggestsPlatformAccess'], false),
    defaultAccessRoleId: readField<string | null>(raw, ['default_access_role_id', 'defaultAccessRoleId'], null),
    defaultAccessRole: null,
    assignedStaffCount: readField<number>(raw, ['assigned_staff_count', 'assignedStaffCount'], 0),
  };
}

function mapStaffPosition(b: BackendStaffPosition): StaffPosition {
  const defaultAccessRoleRaw = b.defaultAccessRole ?? null;
  return {
    id: b.id,
    name: b.name ?? '',
    description: b.description ?? '',
    scopeType: (b.scopeType ?? 'location') as StaffPosition['scopeType'],
    locationId: b.location ?? b.locationId ?? null,
    suggestsPlatformAccess: b.suggestsPlatformAccess ?? false,
    defaultAccessRoleId: b.defaultAccessRoleId ?? null,
    defaultAccessRole: defaultAccessRoleRaw ? {
      id: readField<string>(defaultAccessRoleRaw, ['id'], ''),
      name: readField<string>(defaultAccessRoleRaw, ['name'], ''),
      description: readField<string>(defaultAccessRoleRaw, ['description'], ''),
      scopeType: readField<'company' | 'location'>(defaultAccessRoleRaw, ['scope_type', 'scopeType'], 'location'),
      createdByLocation: readField<string | null>(defaultAccessRoleRaw, ['created_by_location', 'createdByLocation'], null),
    } : null,
    isActive: b.isActive ?? true,
    assignedStaffCount: b.assignedStaffCount ?? b.assigned_staff_count ?? 0,
  };
}

function mapDetailStaff(b: BackendStaffDetail): Staff {
  const person = mapBackendPerson(b.person);
  const staffPosition = mapStaffPositionRaw(b.staffPosition ?? null);
  // Backend ``StaffSerializer`` ships ``location_id`` (camelCased to
  // ``locationId`` by ``apiClient``). The earlier mapper only read
  // ``b.location`` which is the FK *object* ID under DRF defaults
  // when the field IS ``location`` — but here it's ``locationId``.
  // The result was a silent ``locationId=null``, which disabled
  // ``useLocationStaffTokenConfig`` (hook is gated on a non-null id)
  // and hid the Grant/Adjust buttons in ``TokenDebtPanel`` even for
  // company admins. Reading both shapes recovers the value without
  // breaking any existing call site.
  const locationId = b.locationId ?? b.location ?? null;
  return {
    id: b.id,
    person,
    scopeType: (b.scopeType ?? (locationId ? 'location' : 'company')) as Staff['scopeType'],
    locationId,
    staffPositionId: b.staffPositionId ?? staffPosition?.id ?? null,
    staffPosition,
    position: b.position ?? '',
    department: b.department ?? '',
    employmentType: ((b.employmentType) as Staff['employmentType']) ?? 'full_time',
    status: (b.status as Staff['status']) ?? 'active',
    hireDate: b.hireDate ?? '',
    hourlyRate: b.hourlyRate ?? null,
    salary: b.salary ?? null,
    isActive: b.isActive ?? true,
    terminationDate: b.terminationDate ?? b.termination_date ?? null,
    terminationReason: b.terminationReason ?? b.termination_reason ?? '',
    tokenLocationBlacklist: b.tokenLocationBlacklist ?? b.token_location_blacklist ?? [],
    maxDebtOverride: b.maxDebtOverride ?? b.max_debt_override ?? null,
    hideTokenBalanceInPos: b.hideTokenBalanceInPos ?? b.hide_token_balance_in_pos ?? null,
    createdAt: b.createdAt ?? '',
    updatedAt: b.updatedAt ?? '',
    name: person.name,
    email: person.email,
    avatarUrl: person.avatarUrl || undefined,
    locationName: '',
  };
}

function mapListStaff(b: BackendStaffList): Staff {
  const name = b.name ?? '';
  const staffPosition = mapStaffPositionRaw(b.staffPosition ?? null);
  // `undefined` = the payload said nothing, which is NOT the same as "no
  // login". Callers must test `=== false` before offering to create one.
  const hasUser = b.hasUser ?? b.has_user;
  const person = mapBackendPerson({
    id: b.personId ?? b.person_id ?? '',
    name,
    email: b.email ?? '',
    avatar_url: b.avatarUrl ?? b.avatar_url ?? '',
    current_role: 'staff',
    is_active: true,
    has_user: hasUser,
  });
  return {
    id: b.id,
    person,
    hasUser,
    scopeType: (b.scopeType ?? ((b.locationId ?? b.location) ? 'location' : 'company')) as Staff['scopeType'],
    locationId: b.locationId ?? b.location ?? null,
    staffPositionId: b.staffPositionId ?? staffPosition?.id ?? null,
    staffPosition,
    position: b.position ?? '',
    department: b.department ?? '',
    employmentType: ((b.employmentType) as Staff['employmentType']) ?? 'full_time',
    status: (b.status as Staff['status']) ?? 'active',
    hireDate: b.hireDate ?? b.hire_date ?? '',
    hourlyRate: null,
    salary: null,
    isActive: b.isActive ?? b.status === 'active',
    terminationDate: b.terminationDate ?? null,
    terminationReason: '',
    createdAt: '',
    updatedAt: '',
    name,
    email: b.email ?? '',
    avatarUrl: b.avatarUrl ?? b.avatar_url ?? undefined,
    locationName: '',
  };
}

function mapStaffExpense(b: BackendStaffExpense): StaffExpense {
  return {
    id: b.id,
    staffId: b.staff,
    credentialId: b.credential ?? null,
    sourceType: b.sourceType ?? null,
    sourceObjectId: b.sourceObjectId ?? null,
    amount: b.amount,
    description: b.description,
    status: b.status,
    settlementPaymentId: b.settlementPayment ?? null,
    pendingObligationId: b.pendingObligation ?? null,
    settlementType: b.settlementType ?? null,
    createdAt: b.createdAt ?? '',
  };
}

function mapStaffTokenConfig(b: BackendLocationStaffTokenConfig): LocationStaffTokenConfig {
  return {
    id: b.id,
    locationId: b.locationId ?? b.location,
    enabled: b.enabled,
    tokenValueAmount: b.tokenValueAmount ?? '0.00',
    tokenValueCurrency: b.tokenValueCurrency ?? 'EUR',
    weeklyGrantEnabled: b.weeklyGrantEnabled ?? false,
    weeklyGrantAmount: b.weeklyGrantAmount ?? 0,
    weeklyGrantDayOfWeek: b.weeklyGrantDayOfWeek ?? 1,
    weeklyGrantTime: b.weeklyGrantTime ?? null,
    grantAfterDebtSettled: b.grantAfterDebtSettled ?? b.grant_after_debt_settled ?? false,
    carryOverEnabled: b.carryOverEnabled ?? false,
    carryOverMax: b.carryOverMax ?? 0,
    debtEnabled: b.debtEnabled ?? false,
    debtLimit: b.debtLimit ?? 0,
    debtOnGrantPolicy: b.debtOnGrantPolicy ?? 'offset_debt',
    debtSettlementMethods: b.debtSettlementMethods ?? b.debt_settlement_methods ?? ['cash', 'card', 'transfer'],
    manualGrantsEnabled: b.manualGrantsEnabled ?? false,
    transfersEnabled: b.transfersEnabled ?? false,
    transferRequiresApproval: b.transferRequiresApproval ?? false,
    newStaffGrantPolicy: b.newStaffGrantPolicy ?? 'full_grant',
    inactiveStaffPolicy: b.inactiveStaffPolicy ?? 'freeze',
    showTotalsInPos: b.showTotalsInPos ?? true,
    debtAlertThreshold: b.debtAlertThreshold ?? b.debt_alert_threshold ?? 0,
    createdAt: b.createdAt,
    updatedAt: b.updatedAt,
  };
}

function mapStaffTokenBalance(b: BackendStaffTokenBalance): StaffTokenBalance {
  return {
    id: b.id,
    locationId: b.locationId ?? b.location_id ?? b.location ?? '',
    staffId: b.staffId ?? b.staff_id ?? b.staff ?? '',
    staffName: b.staffName ?? b.staff_name ?? '',
    availableTokens: b.availableTokens ?? 0,
    debtTokens: b.debtTokens ?? 0,
    lastGrantAt: b.lastGrantAt ?? null,
    updatedAt: b.updatedAt,
  };
}

function mapStaffTokenLedgerEntry(b: BackendStaffTokenLedgerEntry): StaffTokenLedgerEntry {
  return {
    id: b.id,
    locationId: b.locationId ?? b.location_id ?? b.location ?? '',
    staffId: b.staffId ?? b.staff_id ?? b.staff ?? '',
    staffName: b.staffName ?? b.staff_name ?? '',
    origin: b.origin,
    status: b.status ?? 'approved',
    tokenDelta: b.tokenDelta ?? 0,
    availableAfter: b.availableAfter ?? 0,
    debtAfter: b.debtAfter ?? 0,
    contentType: b.contentType ?? null,
    objectId: b.objectId ?? null,
    notes: b.notes ?? '',
    metadata: b.metadata ?? {},
    createdById: b.createdById ?? null,
    expiresAt: b.expiresAt ?? null,
    createdAt: b.createdAt,
  };
}

// ---------------------------------------------------------------------------
// Service functions
// ---------------------------------------------------------------------------

export async function getAllStaff(filters?: StaffListFilters): Promise<Staff[]> {
  const params: Record<string, string | number | boolean> = { ...buildStaffListParams(filters), page_size: 200 };

  const response = await apiClient.get<{ results: BackendStaffList[] }>(
    '/api/v1/staff/',
    { params },
  );
  return response.data.results.map(mapListStaff);
}

export interface StaffStatusCounts {
  total: number;
  active: number;
  onLeave: number;
  inactive: number;
}

function buildStaffListParams(filters?: StaffListFilters): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {};
  if (filters?.search) params.search = filters.search;
  if (filters?.status) params.status = filters.status;
  if (filters?.department) params.department = filters.department;
  if (filters?.staffPositionId) params.staff_position = filters.staffPositionId;
  if (filters?.scopeType) params.scope_type = filters.scopeType;
  if (filters?.locationId) params.location = filters.locationId;
  // Sorting is the server's job: the table is paginated, so ordering the
  // loaded pages in the browser would rank only the rows that happen to be
  // in memory. Callers pass a value from `STAFF_ORDERING` — anything the
  // server does not list in `ordering_fields` is ignored by DRF, which would
  // silently leave the list in its default order.
  if (filters?.ordering) params.ordering = filters.ordering;
  if (filters?.includeTerminated) params.include_terminated = 'true';
  if (filters?.isActive !== undefined) params.is_active = filters.isActive;
  return params;
}

async function countStaff(filters?: StaffListFilters, status?: StaffStatus): Promise<number> {
  const response = await apiClient.get<{ count: number }>('/api/v1/staff/', {
    params: { ...buildStaffListParams(filters), ...(status ? { status } : {}), page_size: 1 },
  });
  return response.data.count;
}

/**
 * Summary-card counts for a staff scope, taken from the list endpoint's own
 * `count` so they can never disagree with the paginated table below them.
 * Counting a `page_size=200` fetch client-side silently under-reports any
 * scope with more than 200 employees.
 */
export async function getStaffStatusCounts(filters?: StaffListFilters): Promise<StaffStatusCounts> {
  const [total, active, onLeave, inactive] = await Promise.all([
    countStaff(filters),
    countStaff(filters, 'active'),
    countStaff(filters, 'on_leave'),
    countStaff(filters, 'inactive'),
  ]);
  return { total, active, onLeave, inactive };
}

interface StaffFetchOptions {
  skipLocationFilter?: boolean;
}

export async function getStaffById(staffId: string, options?: StaffFetchOptions): Promise<Staff | null> {
  try {
    const response = await apiClient.get<BackendStaffDetail>(`/api/v1/staff/${staffId}/`, {
      skipLocationFilter: options?.skipLocationFilter,
    });
    return mapDetailStaff(response.data);
  } catch (error: unknown) {
    if ((error as ApiError)?.status === 404) return null;
    throw error;
  }
}

export async function getStaffByPersonId(personId: string, options?: StaffFetchOptions): Promise<Staff | null> {
  const response = await apiClient.get<{ results: BackendStaffList[] }>(
    '/api/v1/staff/',
    {
      params: { person: personId, page_size: 1 },
      skipLocationFilter: options?.skipLocationFilter,
    },
  );
  const first = response.data.results[0];
  if (!first) return null;
  const detail = await getStaffById(first.id, options);
  return detail ?? null;
}

export async function getStaffPositions(filters: StaffPositionFilters = {}): Promise<StaffPosition[]> {
  const params: Record<string, string> = { page_size: '200' };
  if (filters.scopeType) params.scope_type = filters.scopeType;
  if (filters.locationId) params.location = filters.locationId;
  if (typeof filters.isActive === 'boolean') params.is_active = String(filters.isActive);

  const response = await apiClient.get<{ results: BackendStaffPosition[] }>(
    '/api/v1/staff-positions/',
    { params },
  );
  return response.data.results.map(mapStaffPosition);
}

export async function createStaffPosition(input: StaffPositionUpsertInput): Promise<StaffPosition> {
  const response = await apiClient.post<BackendStaffPosition>('/api/v1/staff-positions/', {
    name: input.name,
    description: input.description ?? '',
    scope_type: input.scopeType,
    location_id: input.scopeType === 'location' ? (input.locationId ?? null) : null,
    suggests_platform_access: input.suggestsPlatformAccess ?? false,
    default_access_role_id: input.defaultAccessRoleId ?? null,
    is_active: input.isActive ?? true,
  });
  return mapStaffPosition(response.data);
}

export async function updateStaffPosition(positionId: string, input: Partial<StaffPositionUpsertInput>): Promise<StaffPosition> {
  const payload: Record<string, unknown> = {};
  if (input.name !== undefined) payload.name = input.name;
  if (input.description !== undefined) payload.description = input.description;
  if (input.scopeType !== undefined) payload.scope_type = input.scopeType;
  if (input.isActive !== undefined) payload.is_active = input.isActive;
  if (input.locationId !== undefined) payload.location_id = input.locationId;
  if (input.suggestsPlatformAccess !== undefined) payload.suggests_platform_access = input.suggestsPlatformAccess;
  if (input.defaultAccessRoleId !== undefined) payload.default_access_role_id = input.defaultAccessRoleId;

  const response = await apiClient.patch<BackendStaffPosition>(`/api/v1/staff-positions/${positionId}/`, payload);
  return mapStaffPosition(response.data);
}

export async function deleteStaffPosition(positionId: string): Promise<void> {
  await apiClient.delete(`/api/v1/staff-positions/${positionId}/`);
}

export async function getStaffExpensesByStaffId(
  staffId: string,
  options?: StaffFetchOptions,
): Promise<StaffExpense[]> {
  const response = await apiClient.get<{ results: BackendStaffExpense[] }>(
    '/api/v1/staff-expenses/',
    {
      params: { staff: staffId, page_size: 200 },
      skipLocationFilter: options?.skipLocationFilter,
    },
  );
  return response.data.results.map(mapStaffExpense);
}

export async function chargeStaffSellable(
  input: StaffChargeSellableInput,
): Promise<StaffChargeSellableResult> {
  const payload: Record<string, unknown> = {
    staff_id: input.staffId,
    quantity: input.quantity,
    ...buildApproverPayload(input),
  };
  if (input.productId) payload.product_id = input.productId;
  if (input.shopProductId) payload.shop_product_id = input.shopProductId;
  if (input.menuRecipeItemId) payload.menu_recipe_item_id = input.menuRecipeItemId;
  if (input.menuProductItemId) payload.menu_product_item_id = input.menuProductItemId;
  if (input.catalogItemId) payload.catalog_item_id = input.catalogItemId;
  if (input.serviceProductId) payload.service_product_id = input.serviceProductId;
  if (input.description) payload.description = input.description;
  if (input.credentialId) payload.credential = input.credentialId;

  const response = await apiClient.post<Record<string, unknown>>(
    '/api/v1/staff-expenses/charge-sellable/',
    payload,
  );
  // apiClient camelCases every response (GET/POST alike), so reads are camelCase-only.
  const data = response.data;
  const expenseRaw = (data.staffExpense ?? null) as Record<string, unknown> | null;
  const tokenSplitRaw = (data.tokenSplit ?? {}) as Record<string, unknown>;

  return {
    staffExpense: expenseRaw ? mapStaffExpense(expenseRaw as unknown as BackendStaffExpense) : null,
    totalAmount: String(data.totalAmount ?? '0'),
    tokenSplit: {
      tokensSpent: Number(tokenSplitRaw?.tokensSpent ?? 0),
      remainingTokenDebt: Number(tokenSplitRaw?.remainingTokenDebt ?? 0),
      coveredAmount: String(tokenSplitRaw?.coveredAmount ?? '0'),
      monetaryRemainder: String(tokenSplitRaw?.monetaryRemainder ?? '0'),
    },
  };
}

/**
 * Every token balance this staff member holds — one per location they consume
 * at. Location staff have exactly one; company-scope staff accrue one wherever
 * they spend, each valued with that location's own token value.
 */
export async function getStaffTokenBalancesByStaffId(
  staffId: string,
  options?: StaffFetchOptions,
): Promise<StaffTokenBalance[]> {
  const response = await apiClient.get<{ results: BackendStaffTokenBalance[] }>(
    '/api/v1/staff-token-balances/',
    {
      params: { staff: staffId, page_size: 200 },
      skipLocationFilter: options?.skipLocationFilter,
    },
  );
  return (response.data.results ?? []).map(mapStaffTokenBalance);
}

export async function getStaffTokenBalanceByStaffId(
  staffId: string,
  options?: StaffFetchOptions,
): Promise<StaffTokenBalance | null> {
  // Was `page_size: 1` — for a company staff with balances at two locations
  // that returned whichever row came first, so the wallet could show one
  // location's debt while the settle cleared another's.
  return pickPrimaryTokenBalance(await getStaffTokenBalancesByStaffId(staffId, options));
}

export async function getStaffTokenLedgerByStaffId(
  staffId: string,
  options?: StaffFetchOptions,
): Promise<StaffTokenLedgerEntry[]> {
  const response = await apiClient.get<{ results: BackendStaffTokenLedgerEntry[] }>(
    '/api/v1/staff-token-ledger/',
    {
      params: { staff: staffId, page_size: 200 },
      skipLocationFilter: options?.skipLocationFilter,
    },
  );
  return response.data.results.map(mapStaffTokenLedgerEntry);
}

// ── Staff wallet activity feed ───────────────────────────────────────────────

interface BackendStaffActivityItem {
  name?: string;
  quantity?: string | number;
  unitPrice?: string;
  lineTotal?: string;
}

interface BackendStaffActivityRow {
  id?: string;
  kind?: StaffActivityRow['kind'];
  group?: StaffActivityRow['group'];
  origin?: string;
  description?: string;
  reference?: string | null;
  tokenDelta?: number | null;
  money?: string | null;
  status?: string | null;
  createdBy?: string | null;
  createdAt?: string;
  locationId?: string | null;
  locationName?: string | null;
  items?: BackendStaffActivityItem[];
  reversed?: boolean;
  voidable?: boolean;
}

function mapStaffActivityRow(r: BackendStaffActivityRow): StaffActivityRow {
  return {
    id: r.id ?? '',
    kind: r.kind ?? 'purchase',
    // The server always sends `group`; the fallback to the coarse `kind` is for
    // a row from a deployment that predates it, and stays deliberately vague
    // rather than guessing which party a payment reached.
    group: r.group ?? r.kind ?? 'purchase',
    origin: r.origin ?? '',
    description: r.description ?? '',
    reference: r.reference ?? null,
    tokenDelta: r.tokenDelta ?? null,
    money: r.money ?? null,
    status: r.status ?? null,
    createdBy: r.createdBy ?? null,
    createdAt: r.createdAt ?? '',
    // Null is a real answer here, not a missing field — see StaffActivityRow.
    locationId: r.locationId ?? null,
    locationName: r.locationName ?? null,
    items: (r.items ?? []).map((item) => ({
      name: item.name ?? '',
      quantity: String(item.quantity ?? ''),
      unitPrice: item.unitPrice ?? '',
      lineTotal: item.lineTotal ?? '',
    })),
    reversed: r.reversed ?? false,
    voidable: r.voidable ?? false,
  };
}

/**
 * Void a manual token movement (grant/adjustment) by posting a compensating
 * correction. Reinforced-authorization gated — carries the same
 * `ApproverCredential` as every other staff-wallet write.
 */
export async function voidTokenLedgerEntry(
  entryId: string,
  input: ApproverCredential,
): Promise<StaffTokenLedgerEntry> {
  const response = await apiClient.post<BackendStaffTokenLedgerEntry>(
    `/api/v1/staff-token-ledger/${entryId}/void/`,
    buildApproverPayload(input),
  );
  return mapStaffTokenLedgerEntry(response.data);
}

export interface StaffActivityQuery extends StaffActivityFilters {
  staffId: string;
  cursor?: string;
}

export async function getStaffActivity(query: StaffActivityQuery): Promise<StaffActivityPage> {
  const params: Record<string, string | number | boolean> = {
    staff_id: query.staffId,
  };
  if (query.type && query.type !== 'all') params.type = query.type;
  // Applied in the database, before the keyset slice — so it composes with the
  // cursor instead of thinning an already-paged page.
  if (query.locationId) params.location_id = query.locationId;
  if (query.from) params.from = query.from;
  if (query.to) params.to = query.to;
  if (query.q) params.q = query.q;
  if (query.unpaid) params.unpaid = true;
  if (query.cursor) params.cursor = query.cursor;
  if (query.limit) params.limit = query.limit;

  const response = await apiClient.get<{
    results: BackendStaffActivityRow[];
    nextCursor: string | null;
  }>('/api/v1/staff-token-balances/activity/', {
    params,
    // The wallet is a tenant-wide identity surface — never inject location_id.
    skipLocationFilter: true,
  });

  return {
    results: (response.data.results ?? []).map(mapStaffActivityRow),
    nextCursor: response.data.nextCursor ?? null,
  };
}

export type TokenDebtSettlementMethod =
  | 'cash'
  | 'card'
  | 'transfer'
  | 'write_off'
  | 'delete';

export const NON_MONETARY_SETTLEMENT_METHODS: TokenDebtSettlementMethod[] = ['write_off', 'delete'];

export interface TokenDebtSettlementInput extends ApproverCredential {
  staffId: string;
  /**
   * WHICH debt is being settled. Company-scope staff hold one balance per
   * location they consume at, each valued with that location's own token value,
   * so the backend needs the location to know which one to clear, what a token
   * is worth and which settle methods that location allows. Omitting it is only
   * safe when the staff owes at a single location.
   */
  locationId?: string | null;
  method: TokenDebtSettlementMethod;
  /** Not applicable for write_off or delete — always clears full debt */
  amount?: number;
  cashBoxId?: string;
  /** Transfer settle routes to a bank SUBACCOUNT (matrix hr/income/transfer). */
  bankSubaccountId?: string;
  cardTerminalId?: string;
}

/**
 * What a scanned band resolves to when collecting token debt at one location.
 *
 * Every money figure here comes from the SETTLING location's own token config,
 * computed by the server in the same place the settle will read it from — so
 * the amount quoted to the operator and the amount charged cannot diverge.
 * `amountDue` and `tokenValueAmount` stay decimal strings end to end: they are
 * money, and a float round-trip is exactly how a cent goes missing.
 */
export interface ScannedStaffTokenDebt {
  staffId: string;
  personId: string;
  displayName: string;
  scopeType: StaffScopeType;
  /** The till's own location — the debt being collected, not "their" location. */
  locationId: string;
  credentialId: string;
  debtTokens: number;
  tokenValueAmount: string;
  tokenValueCurrency: string;
  amountDue: string;
  settlementMethods: TokenSettlementMethod[];
}

/**
 * Scan a band, ask what its owner owes AT THIS LOCATION.
 *
 * The read half of collecting token debt at a till, and the only route to a
 * company-scope employee: they belong to no location's staff list, so the
 * operator cannot pick them from the settle dialog — but they hold a band and
 * they owe money here.
 *
 * `locationId` is always the till's own location and is never inferred from
 * "the one place they owe": the cash lands in THIS location's drawer, so
 * clearing another location's debt from here would book the collection and the
 * debt relief in two different places.
 *
 * The uid travels in the body, never in the query string — it is a credential,
 * and URLs end up in logs. `showErrorToast: false` because every miss (unknown
 * band, not a staff member, nothing owed here) is a state this flow renders
 * inline and then explains; a toast would say it twice and say it worse.
 */
export async function resolveStaffTokenDebtByCredential(input: {
  nfcUid: string;
  locationId: string;
}): Promise<ScannedStaffTokenDebt> {
  const response = await apiClient.post<{
    staffId: string;
    personId: string;
    displayName: string;
    scopeType: StaffScopeType;
    locationId: string;
    credentialId: string;
    debtTokens: number;
    tokenValueAmount: string;
    tokenValueCurrency: string;
    amountDue: string;
    settlementMethods: TokenSettlementMethod[];
  }>(
    '/api/v1/staff-token-balances/debt-by-credential/',
    { nfc_uid: input.nfcUid, location_id: input.locationId },
    { showErrorToast: false },
  );
  const b = response.data;
  return {
    staffId: b.staffId,
    personId: b.personId,
    displayName: b.displayName,
    scopeType: b.scopeType,
    locationId: b.locationId,
    credentialId: b.credentialId,
    debtTokens: b.debtTokens,
    tokenValueAmount: b.tokenValueAmount,
    tokenValueCurrency: b.tokenValueCurrency,
    amountDue: b.amountDue,
    settlementMethods: b.settlementMethods ?? [],
  };
}

/** One other location where a scanned person owes tokens. */
export interface StaffDebtElsewhereRow {
  locationId: string;
  locationName: string;
  debtTokens: number;
  /** `null` when that location never priced its token — unknown, not free. */
  tokenValueAmount: number | null;
  /** Empty when that location has no currency; never defaulted to EUR. */
  tokenValueCurrency: string;
}

export interface StaffDebtElsewhere {
  staffId: string;
  displayName: string;
  rows: StaffDebtElsewhereRow[];
}

/**
 * After a scan came back "nothing owed here", say what IS true of this band.
 *
 * "No debt at this location" is honest but incomplete: the same person may owe
 * three hundred tokens one camp over, and a till that answers only "nothing
 * here" reads as "this person is square", which is how a debt gets forgotten.
 * So the miss path re-identifies the band and reports the debts that DO exist,
 * naming each location and its amount.
 *
 * Returns `null` when the band cannot be tied to a staff member at all — an
 * unregistered band, or a guest's. The caller then shows the server's own
 * message rather than inventing one.
 *
 * Discloses no more than the viewer may already see: `staff-summary` applies
 * the accessible-location filter server-side, so locations outside their access
 * never appear. Read-only, and it never quotes a figure the settle would use —
 * the amounts here belong to OTHER locations and are informational.
 */
export async function describeStaffDebtElsewhere(input: {
  nfcUid: string;
  excludeLocationId: string;
}): Promise<StaffDebtElsewhere | null> {
  try {
    const scan = await lookupWristband(input.nfcUid, { showErrorToast: false });
    if (!scan.person?.id) return null;
    const staff = await getStaffByPersonId(scan.person.id, { skipLocationFilter: true });
    if (!staff) return null;
    const summary = await getStaffTokenSummary(staff.id);
    return {
      staffId: staff.id,
      displayName: staff.name || scan.person.name,
      rows: summary.byLocation
        .filter((row) => row.debtTokens > 0 && row.locationId !== input.excludeLocationId)
        .map((row) => ({
          locationId: row.locationId,
          locationName: row.locationName,
          debtTokens: row.debtTokens,
          tokenValueAmount: row.tokenValueAmount,
          tokenValueCurrency: row.tokenValueCurrency,
        })),
    };
  } catch {
    // Best-effort enrichment of an error state. If any leg fails the caller
    // still has the server's own message, which is never wrong — only less
    // specific than this would have been.
    return null;
  }
}

export interface TokenDebtSettlementResult {
  resolution: TokenDebtSettlementMethod;
  tokensCleaned: number;
  amount: string;
  balance: StaffTokenBalance | null;
  // >0 when settling released the grant that grant day withheld
  // (LocationStaffTokenConfig.grantAfterDebtSettled). 0 is the usual case.
  releasedGrantTokens: number;
}

export async function settleTokenDebt(input: TokenDebtSettlementInput): Promise<TokenDebtSettlementResult> {
  const payload: Record<string, unknown> = {
    staff_id: input.staffId,
    method: input.method,
    ...buildApproverPayload(input),
  };
  if (input.locationId) payload.location_id = input.locationId;
  if (input.amount !== undefined && !NON_MONETARY_SETTLEMENT_METHODS.includes(input.method)) {
    payload.amount = input.amount;
  }
  if (input.cashBoxId) payload.cash_box_id = input.cashBoxId;
  if (input.bankSubaccountId) payload.bank_subaccount_id = input.bankSubaccountId;
  if (input.cardTerminalId) payload.card_terminal_id = input.cardTerminalId;

  const response = await apiClient.post<{
    resolution: string;
    tokensCleared: number;
    amount: string;
    balance: BackendStaffTokenBalance | null;
    releasedGrantTokens?: number;
  }>('/api/v1/staff-token-balances/settle-debt/', payload);

  const tokensCleared = response.data.tokensCleared ?? 0;
  return {
    resolution: (response.data.resolution ?? input.method) as TokenDebtSettlementMethod,
    tokensCleaned: tokensCleared,
    amount: response.data.amount,
    balance: response.data.balance ? mapStaffTokenBalance(response.data.balance) : null,
    releasedGrantTokens: response.data.releasedGrantTokens ?? 0,
  };
}

export async function getCompanyTokenSummary(): Promise<TokenCompanySummary> {
  const response = await apiClient.get<TokenCompanySummary>(
    '/api/v1/staff-token-balances/company-summary/',
  );
  return response.data;
}

export async function getLocationStaffTokenConfig(locationId: string): Promise<LocationStaffTokenConfig | null> {
  const response = await apiClient.get<{ results: BackendLocationStaffTokenConfig[] }>(
    '/api/v1/staff-token-configs/',
    { params: { location: locationId, page_size: 1 } },
  );
  const first = response.data.results[0];
  return first ? mapStaffTokenConfig(first) : null;
}

export async function upsertLocationStaffTokenConfig(
  input: LocationStaffTokenConfigInput,
  existingId?: string | null,
): Promise<LocationStaffTokenConfig> {
  const payload = {
    location_id: input.locationId,
    enabled: input.enabled,
    token_value_amount: input.tokenValueAmount,
    token_value_currency: input.tokenValueCurrency,
    weekly_grant_enabled: input.weeklyGrantEnabled,
    weekly_grant_amount: input.weeklyGrantAmount,
    weekly_grant_day_of_week: input.weeklyGrantDayOfWeek,
    weekly_grant_time: input.weeklyGrantTime,
    grant_after_debt_settled: input.grantAfterDebtSettled,
    carry_over_enabled: input.carryOverEnabled,
    carry_over_max: input.carryOverMax,
    debt_enabled: input.debtEnabled,
    debt_limit: input.debtLimit,
    debt_on_grant_policy: input.debtOnGrantPolicy,
    debt_settlement_methods: input.debtSettlementMethods,
    manual_grants_enabled: input.manualGrantsEnabled,
    transfers_enabled: input.transfersEnabled,
    transfer_requires_approval: input.transferRequiresApproval,
    show_totals_in_pos: input.showTotalsInPos,
    debt_alert_threshold: input.debtAlertThreshold,
  };

  if (existingId) {
    const response = await apiClient.patch<BackendLocationStaffTokenConfig>(
      `/api/v1/staff-token-configs/${existingId}/`,
      payload,
    );
    return mapStaffTokenConfig(response.data);
  }

  const response = await apiClient.post<BackendLocationStaffTokenConfig>(
    '/api/v1/staff-token-configs/',
    payload,
  );
  return mapStaffTokenConfig(response.data);
}

export async function getStaffTokenBalances(locationId: string): Promise<StaffTokenBalance[]> {
  const response = await apiClient.get<{ results: BackendStaffTokenBalance[] }>(
    '/api/v1/staff-token-balances/',
    { params: { location: locationId, page_size: 200 } },
  );
  return response.data.results.map(mapStaffTokenBalance);
}

export async function getStaffTokenLedger(locationId: string): Promise<StaffTokenLedgerEntry[]> {
  const response = await apiClient.get<{ results: BackendStaffTokenLedgerEntry[] }>(
    '/api/v1/staff-token-ledger/',
    { params: { location: locationId, page_size: 200 } },
  );
  return response.data.results.map(mapStaffTokenLedgerEntry);
}

/**
 * Pending peer token transfers at a location, awaiting manager approval.
 * The backend records each approval-gated transfer as a paired
 * `transfer_out`/`transfer_in` ledger entry sharing a `metadata.transferGroup`;
 * we fetch the pending rows and reconstruct the sender→recipient pairs.
 */
export async function getPendingTokenTransfers(locationId: string): Promise<PendingTokenTransfer[]> {
  const response = await apiClient.get<{ results: BackendStaffTokenLedgerEntry[] }>(
    '/api/v1/staff-token-ledger/',
    { params: { location: locationId, status: 'pending', page_size: 200 } },
  );
  const entries = response.data.results.map(mapStaffTokenLedgerEntry);

  const groups = new Map<string, { out?: StaffTokenLedgerEntry; in?: StaffTokenLedgerEntry }>();
  for (const entry of entries) {
    if (entry.origin !== 'transfer_out' && entry.origin !== 'transfer_in') continue;
    const group = (entry.metadata?.transferGroup as string | undefined) ?? '';
    if (!group) continue;
    const bucket = groups.get(group) ?? {};
    if (entry.origin === 'transfer_out') bucket.out = entry;
    else bucket.in = entry;
    groups.set(group, bucket);
  }

  const transfers: PendingTokenTransfer[] = [];
  groups.forEach(({ out, in: inEntry }, transferGroup) => {
    if (!out || !inEntry) return;
    transfers.push({
      transferGroup,
      ledgerId: out.id,
      locationId: out.locationId,
      fromStaffId: out.staffId,
      fromStaffName: out.staffName,
      toStaffId: inEntry.staffId,
      toStaffName: inEntry.staffName,
      amount: inEntry.tokenDelta,
      notes: out.notes ?? '',
      createdAt: out.createdAt,
    });
  });

  // Newest first.
  return transfers.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
}

export async function approveTokenTransfer(ledgerId: string): Promise<void> {
  await apiClient.post(`/api/v1/staff-token-ledger/${ledgerId}/approve/`, {});
}

export async function rejectTokenTransfer(ledgerId: string, reason?: string): Promise<void> {
  await apiClient.post(`/api/v1/staff-token-ledger/${ledgerId}/reject/`, reason ? { reason } : {});
}

export async function grantStaffTokensManually(
  input: ManualGrantInput,
): Promise<StaffTokenLedgerEntry> {
  const response = await apiClient.post<BackendStaffTokenLedgerEntry>(
    '/api/v1/staff-token-balances/manual-grant/',
    {
      staff_id: input.staffId,
      amount: input.amount,
      ...buildApproverPayload(input),
    },
  );
  return mapStaffTokenLedgerEntry(response.data);
}

export async function adjustStaffTokensManually(
  input: ManualAdjustmentInput,
): Promise<StaffTokenLedgerEntry> {
  const response = await apiClient.post<BackendStaffTokenLedgerEntry>(
    '/api/v1/staff-token-balances/manual-adjustment/',
    {
      staff_id: input.staffId,
      amount: input.amount,
      ...buildApproverPayload(input),
    },
  );
  return mapStaffTokenLedgerEntry(response.data);
}

export async function runWeeklyTokenGrants(
  configId: string,
  input?: WeeklyGrantRunInput,
): Promise<StaffTokenLedgerEntry[]> {
  const response = await apiClient.post<BackendStaffTokenLedgerEntry[]>(
    `/api/v1/staff-token-configs/${configId}/run-weekly-grants/`,
    input?.runAt ? { run_at: input.runAt } : {},
  );
  return response.data.map(mapStaffTokenLedgerEntry);
}

export async function createStaffFromPerson(input: StaffCreateFromPersonInput): Promise<Staff> {
  const response = await apiClient.post<BackendStaffDetail>('/api/v1/staff/', {
    person_id: input.personId,
    scope_type: input.scopeType,
    location_id: input.scopeType === 'location' ? (input.locationId ?? null) : null,
    staff_position_id: input.staffPositionId,
    position: input.position ?? '',
    department: input.department ?? '',
    employment_type: input.employmentType ?? 'full_time',
    status: input.status ?? 'active',
    hire_date: input.hireDate ?? null,
    hourly_rate: input.hourlyRate ?? null,
    salary: input.salary ?? null,
  });

  return mapDetailStaff(response.data);
}

/**
 * The three per-person token exceptions that live on the `Staff` row, as the
 * employee's Tokens tab writes them.
 *
 * `maxDebtOverride` is deliberately `string | null` and never `number`: the
 * backend field is nullable and NULL ("inherit the policy above me") is a
 * different instruction from "0" ("no cap at all — unlimited debt"). Anything
 * that coerces an empty input to a number turns the first into the second and
 * uncaps a person's spending at every location without anyone asking.
 */
export type StaffTokenControlsInput = {
  tokenLocationBlacklist: string[];
  maxDebtOverride: string | null;
  hideTokenBalanceInPos: boolean | null;
};

/**
 * Update a staff member.
 * Personal data → Person endpoint. Staff-specific data → Staff endpoint.
 */
export type UpdateStaffInput = Partial<
  StaffFormData & {
    isActive?: boolean;
    status?: Staff['status'];
    // Token control override fields — live on Staff, not StaffFormData,
    // surfaced here so `StaffTokenRulesCard` can persist them via updateStaff.
    tokenLocationBlacklist?: string[];
    maxDebtOverride?: string | null;
    hideTokenBalanceInPos?: boolean | null;
  }
>;

export async function updateStaff(
  staffId: string,
  data: UpdateStaffInput,
  personId?: string,
): Promise<Staff> {
  const calls: Promise<unknown>[] = [];

  const personalFields: Record<string, unknown> = {};
  if (data.firstName !== undefined || data.lastName !== undefined) {
    personalFields.name = joinName(data.firstName ?? '', data.lastName ?? '');
  }
  if (data.email !== undefined) personalFields.email = data.email;

  if (personId && Object.keys(personalFields).length > 0) {
    calls.push(apiClient.patch(`/api/v1/persons/${personId}/`, personalFields));
  }
  if (personId && data.phone !== undefined) {
    calls.push(syncPersonPrimaryPhone(personId, data.phone));
  }

  const staffFields: Record<string, unknown> = {};
  if (data.status !== undefined) staffFields.status = data.status;
  if (data.department !== undefined) staffFields.department = data.department;
  if (data.position !== undefined) staffFields.position = data.position;
  if (data.staffPositionId !== undefined) staffFields.staff_position_id = data.staffPositionId ?? null;
  if (data.employmentType !== undefined) staffFields.employment_type = data.employmentType;
  if (data.hireDate !== undefined) staffFields.hire_date = data.hireDate || null;
  if (data.salary !== undefined) staffFields.salary = data.salary ?? null;
  if (data.hourlyRate !== undefined) staffFields.hourly_rate = data.hourlyRate ?? null;
  if (data.isActive !== undefined) staffFields.is_active = data.isActive;
  if (data.tokenLocationBlacklist !== undefined) staffFields.token_location_blacklist = data.tokenLocationBlacklist;
  if (data.maxDebtOverride !== undefined) staffFields.max_debt_override = data.maxDebtOverride ?? null;
  if (data.hideTokenBalanceInPos !== undefined) staffFields.hide_token_balance_in_pos = data.hideTokenBalanceInPos;

  if (Object.keys(staffFields).length > 0) {
    calls.push(apiClient.patch(`/api/v1/staff/${staffId}/`, staffFields));
  }

  await Promise.all(calls);
  const response = await apiClient.get<BackendStaffDetail>(`/api/v1/staff/${staffId}/`);
  return mapDetailStaff(response.data);
}

export async function updateStaffStatus(staffId: string, status: Staff['status']): Promise<Staff> {
  return updateStaff(staffId, { status });
}

/**
 * The ONE atomic exit (PENDINGS #22): Staff + login + roles + wristbands in a
 * single backend transaction. 400s carry the pre-flight reason (open token
 * debt, an open cash session, self-termination) — surface them verbatim.
 */
export async function terminateStaff(
  staffId: string,
  input: { effectiveDate?: string; reason?: string } = {},
): Promise<{ staff: Staff; report: StaffTerminationReport }> {
  const response = await apiClient.post<{ staff: Record<string, unknown>; report: StaffTerminationReport }>(
    `/api/v1/staff/${staffId}/terminate/`,
    {
      ...(input.effectiveDate ? { effective_date: input.effectiveDate } : {}),
      ...(input.reason ? { reason: input.reason } : {}),
    },
  );
  return {
    staff: mapDetailStaff(response.data.staff as never),
    report: response.data.report,
  };
}

export async function deleteStaff(staffId: string): Promise<void> {
  await apiClient.delete(`/api/v1/staff/${staffId}/`);
}

export async function getStaffHistory(staffId: string): Promise<StaffHistoryEntry[]> {
  const response = await apiClient.get<{ results: Record<string, unknown>[] }>(
    '/api/v1/staff-history/',
    { params: { staff: staffId, page_size: 100 } },
  );
  return response.data.results.map(h => ({
    id: h.id as string,
    staffId: h.staff as string,
    type: h.type as StaffHistoryEntry['type'],
    title: h.title as string,
    description: (h.description as string) ?? '',
    previousValue: (h.previousValue as string) || undefined,
    newValue: (h.newValue as string) || undefined,
    effectiveDate: h.effectiveDate as string,
    recordedBy: (h.recordedBy as string) || undefined,
    metadata: (h.metadata as Record<string, unknown>) ?? {},
    createdAt: h.createdAt as string,
  }));
}

export async function getStaffPaginated(
  filters?: StaffListFilters,
  cursor?: string,
  limit: number = 20,
): Promise<PaginatedStaffResponse> {
  let endpoint: string;
  let params: Record<string, string | number | boolean> | undefined;

  if (cursor) {
    endpoint = cursor;
  } else {
    endpoint = '/api/v1/staff/';
    params = { ...buildStaffListParams(filters), page_size: limit };
  }

  const response = await apiClient.get<{
    results: BackendStaffList[];
    count: number;
    next: string | null;
  }>(endpoint, params ? { params } : {});

  return {
    data: response.data.results.map(mapListStaff),
    nextCursor: response.data.next,
    hasMore: !!response.data.next,
    totalCount: response.data.count,
  };
}


/**
 * Every staff member assigned to a position, following pagination.
 * Bulk operations (move staff between positions) must not be capped
 * to the first page like the generic list fetch.
 */
export async function getStaffByPosition(staffPositionId: string): Promise<Staff[]> {
  const all: Staff[] = [];
  let page = await getStaffPaginated({ staffPositionId }, undefined, 200);
  all.push(...page.data);
  while (page.hasMore && page.nextCursor) {
    page = await getStaffPaginated({ staffPositionId }, page.nextCursor, 200);
    all.push(...page.data);
  }
  return all;
}

// ── Company-tier staff token policy ──────────────────────────────────────────

interface BackendCompanyStaffTokenConfig {
  id: string;
  enabled: boolean;
  weeklyGrantEnabled?: boolean;
  weekly_grant_enabled?: boolean;
  weeklyGrantAmount?: number;
  weekly_grant_amount?: number;
  weeklyGrantDayOfWeek?: number;
  weekly_grant_day_of_week?: number;
  weeklyGrantTime?: string | null;
  weekly_grant_time?: string | null;
  carryOverEnabled?: boolean;
  carry_over_enabled?: boolean;
  carryOverMax?: number;
  carry_over_max?: number;
  debtEnabled?: boolean;
  debt_enabled?: boolean;
  debtLimit?: number;
  debt_limit?: number;
  debtOnGrantPolicy?: CompanyStaffTokenConfig['debtOnGrantPolicy'];
  debt_on_grant_policy?: CompanyStaffTokenConfig['debtOnGrantPolicy'];
  debtSettlementMethods?: CompanyStaffTokenConfig['debtSettlementMethods'];
  debt_settlement_methods?: CompanyStaffTokenConfig['debtSettlementMethods'];
  manualGrantsEnabled?: boolean;
  manual_grants_enabled?: boolean;
  transfersEnabled?: boolean;
  transfers_enabled?: boolean;
  transferRequiresApproval?: boolean;
  transfer_requires_approval?: boolean;
  newStaffGrantPolicy?: CompanyStaffTokenConfig['newStaffGrantPolicy'];
  new_staff_grant_policy?: CompanyStaffTokenConfig['newStaffGrantPolicy'];
  inactiveStaffPolicy?: CompanyStaffTokenConfig['inactiveStaffPolicy'];
  inactive_staff_policy?: CompanyStaffTokenConfig['inactiveStaffPolicy'];
  showTotalsInPos?: boolean;
  show_totals_in_pos?: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

function mapCompanyStaffTokenConfig(b: BackendCompanyStaffTokenConfig): CompanyStaffTokenConfig {
  return {
    id: b.id,
    enabled: b.enabled,
    weeklyGrantEnabled: b.weeklyGrantEnabled ?? false,
    weeklyGrantAmount: b.weeklyGrantAmount ?? 0,
    weeklyGrantDayOfWeek: b.weeklyGrantDayOfWeek ?? 1,
    weeklyGrantTime: b.weeklyGrantTime ?? null,
    carryOverEnabled: b.carryOverEnabled ?? false,
    carryOverMax: b.carryOverMax ?? 0,
    debtEnabled: b.debtEnabled ?? false,
    debtLimit: b.debtLimit ?? 0,
    debtOnGrantPolicy: b.debtOnGrantPolicy ?? 'offset_debt',
    debtSettlementMethods: b.debtSettlementMethods ?? b.debt_settlement_methods ?? ['cash', 'card', 'transfer'],
    manualGrantsEnabled: b.manualGrantsEnabled ?? false,
    transfersEnabled: b.transfersEnabled ?? false,
    transferRequiresApproval: b.transferRequiresApproval ?? false,
    newStaffGrantPolicy: b.newStaffGrantPolicy ?? 'full_grant',
    inactiveStaffPolicy: b.inactiveStaffPolicy ?? 'freeze',
    showTotalsInPos: b.showTotalsInPos ?? true,
    notes: b.notes ?? '',
    createdAt: b.createdAt,
    updatedAt: b.updatedAt,
  };
}

export async function getCompanyStaffTokenConfig(): Promise<CompanyStaffTokenConfig> {
  const response = await apiClient.get<BackendCompanyStaffTokenConfig>(
    '/api/v1/company-staff-token-config/',
  );
  return mapCompanyStaffTokenConfig(response.data);
}

export async function updateCompanyStaffTokenConfig(
  input: CompanyStaffTokenConfigInput,
): Promise<CompanyStaffTokenConfig> {
  const payload = {
    enabled: input.enabled,
    weekly_grant_enabled: input.weeklyGrantEnabled,
    weekly_grant_amount: input.weeklyGrantAmount,
    weekly_grant_day_of_week: input.weeklyGrantDayOfWeek,
    weekly_grant_time: input.weeklyGrantTime,
    carry_over_enabled: input.carryOverEnabled,
    carry_over_max: input.carryOverMax,
    debt_enabled: input.debtEnabled,
    debt_limit: input.debtLimit,
    debt_on_grant_policy: input.debtOnGrantPolicy,
    debt_settlement_methods: input.debtSettlementMethods,
    manual_grants_enabled: input.manualGrantsEnabled,
    transfers_enabled: input.transfersEnabled,
    transfer_requires_approval: input.transferRequiresApproval,
    show_totals_in_pos: input.showTotalsInPos,
    notes: input.notes,
  };

  const response = await apiClient.patch<BackendCompanyStaffTokenConfig>(
    '/api/v1/company-staff-token-config/',
    payload,
  );
  return mapCompanyStaffTokenConfig(response.data);
}
